import type { PlanTier } from '@/lib/planLimits';
import type { BillingProfileFields } from '@/lib/billing/paid-access';
import { resolveEntitlement } from '@/lib/billing/entitlement';
import {
  isEntitledBillingStatus,
  resolvePlanTier,
  resolvePlanTierFromBillingIdentity,
} from '@/lib/billing/resolve-plan';

/** Row in public.billing_identity_links — paid access source of truth after auth user recreation. */
export type BillingIdentityLink = {
  user_id: string;
  login_email?: string | null;
  stripe_email?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  price_id?: string | null;
  plan?: string | null;
  status?: string | null;
  current_period_end?: string | null;
  link_source?: string | null;
  linked_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

/** Paid access when billing_identity_links.status is active or trialing. */
export function isActiveBillingIdentity(link: BillingIdentityLink | null | undefined): boolean {
  if (!link?.user_id) return false;
  return isEntitledBillingStatus(link.status);
}

/** Plan tier granted by a paid billing identity link (null if not entitled). */
export function tierFromBillingIdentity(
  link: BillingIdentityLink | null | undefined
): PlanTier | null {
  const tier = resolvePlanTierFromBillingIdentity(link);
  return tier === 'free' ? null : tier;
}

/** Effective entitlements: user_profiles paid rows always win over billing_identity_links. */
export function resolveEffectivePlanTier(
  profile: BillingProfileFields | null | undefined,
  billingIdentity: BillingIdentityLink | null | undefined
): PlanTier {
  return resolveEntitlement(profile, billingIdentity).tier;
}

export { resolvePlanTier, resolvePlanTierFromBillingIdentity, isEntitledBillingStatus };

export const BILLING_IDENTITY_SELECT =
  'user_id, stripe_customer_id, stripe_subscription_id, price_id, plan, status, current_period_end';
